import React, { useState } from "react";
import { apiFetch } from "../api/apiFetch";
import { orderRolesAsTree, withParents, withoutRolesAndChildren } from "../utils/roleHierarchy";

export default function InviteModal({ roles, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [roleIds, setRoleIds] = useState([]);
  const [expiryDays, setExpiryDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await apiFetch("/invites", {
        method: "POST",
        body: JSON.stringify({
          email,
          role_ids: roleIds,
          expiry_days: expiryDays,
        }),
      });

      setInviteUrl(result.invite_url);
    } catch (err) {
      setError(err.message || "Fout bij aanmaken uitnodiging");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Nieuwe Gebruiker Uitnodigen</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        {inviteUrl ? (
          <div>
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
              Uitnodiging succesvol aangemaakt!
            </div>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <p className="text-sm text-gray-600 mb-2">Uitnodigingslink:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded text-sm bg-white font-mono text-xs"
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                    copied
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                  }`}
                >
                  {copied ? "Gekopieerd!" : "Kopieer"}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Deel deze link met de gebruiker. De link is {expiryDays} dag{expiryDays !== 1 ? "en" : ""} geldig.
            </p>
            <button
              onClick={() => {
                onSuccess?.();
                onClose();
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="gebruiker@voorbeeld.be"
                required
              />
            </div>

            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-1">Rollen</span>
              <div className="max-h-56 overflow-y-auto rounded border p-2 space-y-1" role="group" aria-label="Rollen">
                {roles.length === 0 && <div className="text-sm text-gray-400 p-1">Rollen laden…</div>}
                {orderRolesAsTree(roles).map((role) => (
                  <label key={role.id} className={`flex items-start gap-2 rounded px-1 py-1 text-sm hover:bg-gray-50 ${role.depth ? "ml-5" : ""}`}>
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={roleIds.includes(role.id)}
                      onChange={() =>
                        setRoleIds((cur) =>
                          cur.includes(role.id)
                            ? withoutRolesAndChildren(cur, [role.id], roles)
                            : withParents([...cur, role.id], roles)
                        )
                      }
                    />
                    <span>
                      <span className="font-medium text-gray-800">{role.name}</span>
                      {role.is_primary && (
                        <span className="ml-2 rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">basis</span>
                      )}
                      {role.category && <span className="ml-2 text-xs text-gray-400">{role.category}</span>}
                      {role.description && <span className="block text-xs text-gray-500">{role.description}</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                De rechten van de gebruiker zijn de som van de gekozen rollen. Een rol onder een basisrol krijgt die basisrol er automatisch bij. Zonder keuze krijgt de gebruiker de rol Read-Only.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Geldig voor
              </label>
              <select
                value={expiryDays}
                onChange={(e) => setExpiryDays(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>1 dag</option>
                <option value={3}>3 dagen</option>
                <option value={7}>7 dagen (standaard)</option>
                <option value={14}>14 dagen</option>
                <option value={30}>30 dagen</option>
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Bezig..." : "Uitnodiging Aanmaken"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
